import { useState } from "react";
// @ts-expect-error pure js
import TextInput from "./TextInput";
import { getIn, useFormikContext } from "formik";
import Button from "./Button";
// @ts-expect-error pure js
import useDebouncedValue from "../../hooks/use-debounced-value";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "./SelectStandalone";
// @ts-expect-error pure js
import useAddressSearch from "../../hooks/use-address-search";
import Text from "./Text";
import styles from "./AddressFields.module.css";

interface AddressFieldsProps {
  namePrefix?: string;
  nameRue?: string;
  dependOn?: string;
  mandatory?: boolean;
}

interface AddressSuggestion {
  rue: string;
  ville: string;
  cp: string;
}

function buildAdresse(suggestion: AddressSuggestion) {
  return `${suggestion.rue}, ${suggestion.ville} (${suggestion.cp})`;
}

export default function AddressFields({
  namePrefix = "",
  nameRue: nameRueProps = "adresse",
  dependOn,
  mandatory,
}: AddressFieldsProps) {
  const { setFieldValue, values, errors, touched, setFieldTouched } =
    useFormikContext();
  const [query, setQuery] = useState("");
  const [showIndividualFields, setShowIndividualFields] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 250);
  const { suggestions, isLoading: isLoadingSuggestions } =
    useAddressSearch(debouncedQuery);

  const nameRue = `${namePrefix}${nameRueProps}`;
  const nameVille = `${namePrefix}ville`;
  const nameCP = `${namePrefix}cp`;
  const nameComplement = `${namePrefix}complement_adresse`;

  const isAddressInvalid =
    (getIn(errors, nameRue) ||
      getIn(errors, nameVille) ||
      getIn(errors, nameCP)) &&
    (getIn(touched, nameRue) ||
      getIn(touched, nameVille) ||
      getIn(touched, nameCP));

  // Handle selecting a suggestion
  async function suggestionSelectHandler(suggestion: AddressSuggestion | null) {
    setQuery("");
    await Promise.all([
      setFieldValue(nameVille, suggestion?.ville ?? ""),
      setFieldValue(nameCP, suggestion?.cp ?? ""),
      setFieldValue(nameRue, suggestion?.rue ?? ""),
    ]);
    return blurSelectHandler();
  }

  function blurSelectHandler() {
    return Promise.all([
      setFieldTouched(nameVille),
      setFieldTouched(nameCP),
      setFieldTouched(nameRue),
    ]);
  }

  const selectSuggestions = [...suggestions];
  let selectValue: AddressSuggestion | "" = "";
  const valueRue = getIn(values, nameRue);
  const valueVille = getIn(values, nameVille);
  const valueCP = getIn(values, nameCP);
  if (valueRue && valueVille && valueCP) {
    selectValue = {
      ville: valueVille,
      cp: valueCP,
      rue: valueRue,
    };
  }

  return (
    <div className={styles.fields}>
      <div hidden={showIndividualFields}>
        <ReactSelectStandalone
          components={{ DropdownIndicator: null }}
          options={selectSuggestions}
          filterOption={null}
          value={selectValue}
          isClearable
          onBlur={blurSelectHandler}
          getOptionLabel={buildAdresse}
          getOptionValue={buildAdresse}
          onInputChange={(value: string) => setQuery(value)}
          onChange={suggestionSelectHandler}
          loadingMessage={() => <>Chargement des suggestions</>}
          noOptionsMessage={({ inputValue }: { inputValue: string }) =>
            inputValue.length > 3 ? <>Aucune suggestion trouvée</> : null
          }
          placeholder="13 rue de la colline, Nancy (54000)"
          isLoading={isLoadingSuggestions}
          isInvalid={isAddressInvalid}
          error={"Veuillez sélectionner une adresse valide"}
          label={"Adresse"}
          mandatory={mandatory ?? true}
        />
        <Text variant="deci" color="muted">
          Votre adresse n'apparaît pas ?{" "}
          <Button
            type={"button"}
            variant="tertiary"
            onClick={() => setShowIndividualFields(true)}
          >
            Entrer manuellement
          </Button>
        </Text>
      </div>
      <TextInput
        name={nameRue}
        label={"Adresse"}
        placeholder={"13 Rue de la colline"}
        dependon={dependOn}
        hidden={!showIndividualFields}
        marginForm=""
      />
      <TextInput
        name={nameComplement}
        label={"Complément d'adresse"}
        placeholder={"Batiment B"}
        dependon={dependOn}
        marginForm=""
      />
      <div className={styles.row}>
        <TextInput
          type="text"
          name={nameCP}
          label={"Code postal"}
          placeholder="27290"
          dependon={dependOn}
          mandatory={mandatory}
          hidden={!showIndividualFields}
          marginForm=""
        />
        <TextInput
          type="text"
          name={nameVille}
          label={"Ville"}
          placeholder="Thénouville"
          dependon={dependOn}
          mandatory={mandatory}
          hidden={!showIndividualFields}
          marginForm=""
        />
      </div>
    </div>
  );
}
